import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTable } from '@/components/ui/data-table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { Plus, MoreHorizontal, Eye, Edit, Trash2, Copy, Key, Globe, Shield } from 'lucide-react'
import { format } from 'date-fns'
import { ColumnDef } from '@tanstack/react-table'

// API Key表单验证
const apiKeySchema = z.object({
  name: z.string().min(1, '请输入API Key名称'),
  description: z.string().optional(),
  allowedApis: z.array(z.string()).min(1, '请至少选择一个API权限'),
  rateLimitPerSecond: z.number().min(1, '每秒请求数至少为1').max(1000, '每秒请求数不能超过1000'),
  webhookUrl: z.string().url().optional().or(z.literal('')),
  webhookSecret: z.string().optional(),
})

type ApiKeyForm = z.infer<typeof apiKeySchema>

// API Key数据接口
interface ApiKey {
  id: number
  appKey: string
  appSecret?: string // 只在创建时返回
  keyName: string
  description?: string
  status: string
  allowedApis: string
  rateLimitPerSecond: number
  webhookUrl?: string
  webhookSecret?: string
  expiresAt?: string
  lastUsedAt?: string
  createdAt: string
}

// 可用的API权限
const availableApis = [
  { value: 'sms:send', label: '短信发送' },
  { value: 'sms:query', label: '短信查询' },
  { value: 'account:balance', label: '账户余额' },
  { value: 'account:transactions', label: '交易记录' },
  { value: 'template:list', label: '模板列表' },
  { value: 'signature:list', label: '签名列表' },
]

export default function ApiKeyManagement() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { toast } = useToast()

  // 创建API Key表单
  const createForm = useForm<ApiKeyForm>({
    resolver: zodResolver(apiKeySchema),
    defaultValues: {
      allowedApis: ['sms:send', 'sms:query'],
      rateLimitPerSecond: 10,
    },
  })

  // 编辑API Key表单
  const editForm = useForm<ApiKeyForm>({
    resolver: zodResolver(apiKeySchema),
  })

  // 加载API Key列表
  const loadApiKeys = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/v1/tenant/api-keys?page=1&size=100', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('tenant_token')}`,
        },
      })
      
      if (response.ok) {
        const data = await response.json()
        setApiKeys(data.records || [])
      }
    } catch (error) {
      toast({
        title: '加载失败',
        description: '无法加载API Key列表',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  // 创建API Key
  const handleCreateApiKey = async (data: ApiKeyForm) => {
    try {
      const response = await fetch('/api/v1/tenant/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('tenant_token')}`,
        },
        body: JSON.stringify(data),
      })

      if (response.ok) {
        const newApiKey = await response.json()
        setApiKeys(prev => [newApiKey, ...prev])
        setIsCreateDialogOpen(false)
        createForm.reset()
        
        toast({
          title: '创建成功',
          description: 'API Key已创建，请妥善保管AppSecret',
        })
      } else {
        throw new Error('创建失败')
      }
    } catch (error) {
      toast({
        title: '创建失败',
        description: '请稍后重试',
        variant: 'destructive',
      })
    }
  }

  // 更新API Key状态
  const handleUpdateStatus = async (id: number, status: string) => {
    try {
      const response = await fetch(`/api/v1/tenant/api-keys/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('tenant_token')}`,
        },
        body: JSON.stringify({ status }),
      })

      if (response.ok) {
        setApiKeys(prev => prev.map(key => 
          key.id === id ? { ...key, status } : key
        ))
        
        toast({
          title: '更新成功',
          description: `API Key状态已更新为${status === 'ACTIVE' ? '启用' : '禁用'}`,
        })
      }
    } catch (error) {
      toast({
        title: '更新失败',
        description: '请稍后重试',
        variant: 'destructive',
      })
    }
  }

  // 删除API Key
  const handleDeleteApiKey = async (id: number) => {
    if (!confirm('确定要删除这个API Key吗？删除后无法恢复。')) {
      return
    }

    try {
      const response = await fetch(`/api/v1/tenant/api-keys/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('tenant_token')}`,
        },
      })

      if (response.ok) {
        setApiKeys(prev => prev.filter(key => key.id !== id))
        toast({
          title: '删除成功',
          description: 'API Key已删除',
        })
      }
    } catch (error) {
      toast({
        title: '删除失败',
        description: '请稍后重试',
        variant: 'destructive',
      })
    }
  }

  // 复制到剪贴板
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast({
      title: '已复制',
      description: '内容已复制到剪贴板',
    })
  }

  // 表格列定义
  const columns: ColumnDef<ApiKey>[] = [
    {
      accessorKey: 'keyName',
      header: '名称',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Key className="h-4 w-4 text-blue-500" />
          <span>{row.getValue('keyName')}</span>
        </div>
      ),
    },
    {
      accessorKey: 'appKey',
      header: 'App Key',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
            {row.getValue('appKey')}
          </code>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => copyToClipboard(row.getValue('appKey'))}
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: '状态',
      cell: ({ row }) => {
        const status = row.getValue('status')
        return (
          <Badge variant={status === 'ACTIVE' ? 'success' : 'secondary'}>
            {status === 'ACTIVE' ? '启用' : '禁用'}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'rateLimitPerSecond',
      header: '限流',
      cell: ({ row }) => `${row.getValue('rateLimitPerSecond')}/秒`,
    },
    {
      accessorKey: 'webhookUrl',
      header: 'Webhook',
      cell: ({ row }) => {
        const url = row.getValue('webhookUrl')
        return url ? (
          <div className="flex items-center space-x-2">
            <Globe className="h-4 w-4 text-green-500" />
            <span className="text-sm text-gray-600">已配置</span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">未配置</span>
        )
      },
    },
          {
        accessorKey: 'lastUsedAt',
        header: '最后使用',
        cell: ({ row }) => {
          const lastUsedAt = row.getValue('lastUsedAt') as string
          return lastUsedAt ? format(new Date(lastUsedAt), 'yyyy-MM-dd HH:mm') : '-'
        },
      },
          {
        accessorKey: 'createdAt',
        header: '创建时间',
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt') as string
          return format(new Date(createdAt), 'yyyy-MM-dd HH:mm')
        },
      },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        const apiKey = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setSelectedApiKey(apiKey)
                setIsViewDialogOpen(true)
              }}>
                <Eye className="mr-2 h-4 w-4" />
                查看详情
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setSelectedApiKey(apiKey)
                editForm.reset({
                  name: apiKey.keyName,
                  description: apiKey.description || '',
                  allowedApis: JSON.parse(apiKey.allowedApis || '[]'),
                  rateLimitPerSecond: apiKey.rateLimitPerSecond,
                  webhookUrl: apiKey.webhookUrl || '',
                  webhookSecret: apiKey.webhookSecret || '',
                })
                setIsEditDialogOpen(true)
              }}>
                <Edit className="mr-2 h-4 w-4" />
                编辑
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleUpdateStatus(apiKey.id, 
                apiKey.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}>
                <Shield className="mr-2 h-4 w-4" />
                {apiKey.status === 'ACTIVE' ? '禁用' : '启用'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDeleteApiKey(apiKey.id)}>
                <Trash2 className="mr-2 h-4 w-4" />
                删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  // 页面加载时获取数据
  useState(() => {
    loadApiKeys()
  })

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <PageHeader 
        title="API Key管理"
        description="管理您的API密钥，用于第三方系统集成"
      />

      {/* 功能说明 */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          API Key用于第三方系统集成，请妥善保管AppSecret。建议定期轮换密钥以确保安全。
        </AlertDescription>
      </Alert>

      {/* 操作栏 */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          共 {apiKeys.length} 个API Key
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          创建API Key
        </Button>
      </div>

      {/* API Key列表 */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">加载中...</div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={apiKeys}
            />
          )}
        </CardContent>
      </Card>

      {/* 创建API Key对话框 */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>创建API Key</DialogTitle>
            <DialogDescription>
              创建一个新的API Key用于第三方系统集成
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={createForm.handleSubmit(handleCreateApiKey)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>名称 *</Label>
                <Input {...createForm.register('name')} placeholder="请输入API Key名称" />
                {createForm.formState.errors.name && (
                  <p className="text-sm text-red-600">{createForm.formState.errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>每秒请求数 *</Label>
                <Input 
                  type="number" 
                  {...createForm.register('rateLimitPerSecond', { valueAsNumber: true })}
                  placeholder="10"
                />
                {createForm.formState.errors.rateLimitPerSecond && (
                  <p className="text-sm text-red-600">{createForm.formState.errors.rateLimitPerSecond.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>描述</Label>
              <Textarea {...createForm.register('description')} placeholder="可选描述信息" />
            </div>

            <div className="space-y-2">
              <Label>API权限 *</Label>
              <div className="grid grid-cols-2 gap-2">
                {availableApis.map(api => (
                  <label key={api.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={api.value}
                      {...createForm.register('allowedApis')}
                    />
                    <span className="text-sm">{api.label}</span>
                  </label>
                ))}
              </div>
              {createForm.formState.errors.allowedApis && (
                <p className="text-sm text-red-600">{createForm.formState.errors.allowedApis.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <Input {...createForm.register('webhookUrl')} placeholder="https://your-domain.com/webhook" />
              {createForm.formState.errors.webhookUrl && (
                <p className="text-sm text-red-600">{createForm.formState.errors.webhookUrl.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Webhook密钥</Label>
              <Input {...createForm.register('webhookSecret')} placeholder="用于验证webhook请求的密钥" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">创建</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 查看API Key详情对话框 */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>API Key详情</DialogTitle>
          </DialogHeader>
          
          {selectedApiKey && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">名称</Label>
                  <p className="text-sm text-muted-foreground">{selectedApiKey.keyName}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">状态</Label>
                  <Badge variant={selectedApiKey.status === 'ACTIVE' ? 'success' : 'secondary'}>
                    {selectedApiKey.status === 'ACTIVE' ? '启用' : '禁用'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">App Key</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded flex-1">
                    {selectedApiKey.appKey}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(selectedApiKey.appKey)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {selectedApiKey.description && (
                <div>
                  <Label className="text-sm font-medium">描述</Label>
                  <p className="text-sm text-muted-foreground">{selectedApiKey.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">每秒请求数</Label>
                  <p className="text-sm text-muted-foreground">{selectedApiKey.rateLimitPerSecond}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">最后使用时间</Label>
                  <p className="text-sm text-muted-foreground">
                    {selectedApiKey.lastUsedAt ? format(new Date(selectedApiKey.lastUsedAt), 'yyyy-MM-dd HH:mm:ss') : '从未使用'}
                  </p>
                </div>
              </div>

              {selectedApiKey.webhookUrl && (
                <div>
                  <Label className="text-sm font-medium">Webhook URL</Label>
                  <p className="text-sm text-muted-foreground break-all">{selectedApiKey.webhookUrl}</p>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium">创建时间</Label>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(selectedApiKey.createdAt), 'yyyy-MM-dd HH:mm:ss')}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
